import React, { useEffect, useState } from "react";
import { Bell, HelpCircle, MessageCircle } from "lucide-react";
import Footer from "../../components/ui/Footer";
import Header from "../../components/ui/Header";
import NotificationSection from "../../components/cs/NotificationSection";
import FAQSection from "../../components/cs/FAQSection";
import ODOSection from "../../components/cs/ODOSection";
import SidebarMenu, { MenuItemType } from "../../components/common/SidebarMenu";
import { useLocation, useNavigate } from "react-router-dom";

type TabType = "notification" | "faq" | "odo";

const CSPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabType>("notification");

  useEffect(() => {
    const state = location.state as { activeTab?: TabType };
    if (state?.activeTab) {
      setActiveTab(state.activeTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const menuItems: MenuItemType[] = [
    {
      id: "notification",
      label: "공지사항",
      icon: <Bell className="w-4 h-4" />,
    },
    {
      id: "faq",
      label: "자주하는 질문",
      icon: <HelpCircle className="w-4 h-4" />,
      mobileLabel: "자주하는<br/>질문",
    },
    {
      id: "odo",
      label: "1:1 문의",
      icon: <MessageCircle className="w-4 h-4" />,
    },
  ];

  const getPageTitle = () => {
    const item = menuItems.find((item) => item.id === activeTab);
    return item?.label || "공지사항";
  };

  const getPageSubtitle = () => {
    switch (activeTab) {
      case "notification":
        return "Aivy의 공지사항을 확인해보세요.";
      case "faq":
        return "자주 묻는 질문들을 확인해보세요.";
      case "odo":
        return "궁금한 점이 있으시면 언제든 문의해주세요.";
      default:
        return "공지사항을 확인해보세요.";
    }
  };

  const handleCreateInquiry = () => {
    navigate("/cs/odo/create");
  };

  return (
    <>
      <Header />
      <main className="mx-auto px-5 md:px-6 py-8 lg:py-12 relative max-w-6xl mb-[100px] font-serif-kr">
        <div className="mb-4 px-2 text-center lg:text-left">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-teal-600/60 via-green-500 to-emerald-600 bg-clip-text text-transparent mb-2">
            고객센터
          </h1>
          <p className="text-gray-600 text-sm md:text-lg">궁금한 점이 있으시면 언제든 문의해주세요.</p>
        </div>
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <SidebarMenu
            title="고객센터"
            menuItems={menuItems}
            activeItem={activeTab}
            onItemClick={(itemId) => setActiveTab(itemId as TabType)}
            actionButton={{
              label: "1:1 문의하기",
              onClick: handleCreateInquiry,
            }}
          />

          {/* 메인 콘텐츠 */}
          <div className="flex-1 min-w-0">
            <div className="bg-white border border-gray-200 rounded-xl">
              <div className="px-4 md:px-8 py-6 border-b border-gray-200">
                <h1 className="text-xl md:text-2xl font-bold mb-2">
                  {getPageTitle()}
                </h1>
                <p className="text-base md:text-lg text-gray-800">
                  {getPageSubtitle()}
                </p>
              </div>

              <div className="p-4 md:p-8">
                {activeTab === "notification" && <NotificationSection />}
                {activeTab === "faq" && <FAQSection />}
                {activeTab === "odo" && <ODOSection />}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CSPage;
